// @flow

import * as React from "react";
import useMergableState from "../../hooks/useMergableState";
import { Row, Col } from "@components/organisms/Grid";
import { ReactForm, ReactFormField } from "@adrenalin/react-form";
import GoogleAddressInput from "@components/atoms/GoogleAddressInput";
import iconPlus from "../../../assets/images/icon-plus.png";
import iconMinus from "../../../assets/images/icon-minus.png";
import getSessionToken from "../../utils/getSessionToken";

// store quantities in local storage
const local_toolkit_quantities = localStorage.getItem("toolkit_quantities");
const localFormQuantities = local_toolkit_quantities
    ? JSON.parse(local_toolkit_quantities)
    : {};

type PropsType = {
    action: ?string,
    hiddenInputs: ?any,
    public: ?boolean,
};

const ToolkitOrderForm = (props: PropsType): React.Node => {
    const deliveryAddressRef = React.useRef();
    const cityRef = React.useRef();
    const stateRef = React.useRef();
    const postcodeRef = React.useRef();
    const formRef = React.useRef();
    const defaultInputValues =
        localFormQuantities[window.location.pathname] || {};
    const [formLightbox, setFormLightbox] = React.useState(false);
    const [formError, setFormError] = React.useState("");
    const [inputValues, setInputValues] = useMergableState(defaultInputValues);

    const [fullAddressEditing, setfullAddressEditing] = React.useState(false);

    const handleGoogleAddressInputChange = event => {
        const addressComponents = event.target.data;
        console.log(event);
        if (addressComponents && addressComponents.address_components) {
            //map the google address object data to the address sub fields
            if (deliveryAddressRef.current) {
                deliveryAddressRef.current.value = addressComponents.name;
            }
            if (cityRef.current) {
                cityRef.current.value = addressComponents.vicinity;
            }
            if (stateRef.current) {
                stateRef.current.value = addressComponents.address_components[4]
                    ? addressComponents.address_components[4].short_name
                    : "";
            }
            if (postcodeRef.current) {
                postcodeRef.current.value = addressComponents
                    .address_components[6]
                    ? addressComponents.address_components[6].long_name
                    : "";
            }
        }
    };

    const handleCantFindAddressClick = () => {
        // toggle the address area
        setfullAddressEditing(!fullAddressEditing);
    };

    const handleFormSuccess = (formData, fields, event) => {
        event.preventDefault();
        const formBody = new FormData(formRef.current.formRef.current);
        if (props.public === true) {
            fetch(props.endpoint_url, {
                method: "POST",
                body: formBody,
            })
                .then(data => {
                    event.preventDefault();
                    setFormLightbox(true);
                    clearForm();
                })
                .catch(res => {
                    // setFetching(false);
                    setFormError("Sending form failed");
                    console.error(res);
                });
        } else {
            getSessionToken()
                .then(token => {
                    fetch(props.endpoint_url, {
                        headers: {
                            "X-CSRF-Token": token,
                        },
                        method: "POST",
                        body: formBody,
                    })
                        .then(data => {
                            event.preventDefault();
                            setFormLightbox(true);
                            clearForm();
                        })
                        .catch(res => {
                            // setFetching(false);
                            setFormError("Sending form failed");
                            console.error(res);
                        });
                })
                .catch(res => {
                    setFormError("Error catching token");
                });
        }
    };

    const clearForm = () => {
        const numberValues = {};
        props.resource_data.map((data, key): number => {
            numberValues["resource_" + key] = 0;
            return key;
        });
        setInputValues(numberValues);
        formRef.current.formRef.current.reset();
    };

    const validateNumber = (key): number => {
        return parseInt(
            Math.max(parseInt(inputValues["resource_" + key] || 0), 0)
        );
    };

    const changeNumber = (key, amount): number => {
        setInputValues({
            ["resource_" + key]: validateNumber(key) + amount,
        });
    };

    const handleQuantityFocus = (e, key): any => {
        e.target.select();
    };

    const handleQuantityBlur = (e, key): any => {};

    React.useEffect(() => {
        // store quantities in local storage
        localStorage.setItem(
            "toolkit_quantities",
            JSON.stringify({
                [window.location.pathname]: inputValues,
            })
        );
    }, [inputValues]);

    const renderResourceField = (resourceData, key): React.Node => {
        return (
            <div className="resource-field" key={key}>
                <div className="resource-field__image">
                    {resourceData.image && (
                        <img
                            src={resourceData.image.thumb}
                            alt={resourceData.image.title}
                        />
                    )}
                </div>

                <div className="resource-field__copy">
                    <h3 className="heading-4">{resourceData.name}</h3>
                    <div
                        className="main-content__main-summary rte"
                        dangerouslySetInnerHTML={{
                            __html: resourceData.summary,
                        }}
                    ></div>
                </div>
                <div className="resource-field__field">
                    <input
                        type="hidden"
                        name={`resource_qty[items][${key}][_item_][resource]`}
                        value={resourceData.tid}
                    />
                    <button
                        type="button"
                        className="resource-field__increment-button"
                        disabled={(inputValues["resource_" + key] || 0) <= 0}
                        onClick={(event: any) => {
                            event.preventDefault();
                            changeNumber(key, -1);
                        }}
                    >
                        <img src={iconMinus} alt="Decrement" />
                    </button>
                    <ReactFormField
                        type="number"
                        name={`resource_qty[items][${key}][_item_][quantity]`}
                        value={validateNumber(key)}
                        min={0}
                        onChange={(event: any) => {
                            setInputValues({
                                ["resource_" + key]: event.target.value,
                            });
                        }}
                        onFocus={(e: any): any => handleQuantityFocus(e, key)}
                        onBlur={(e: any): any => handleQuantityBlur(e, key)}
                    />
                    <button
                        type="button"
                        className="resource-field__increment-button"
                        onClick={(event: any) => {
                            event.preventDefault();
                            changeNumber(key, 1);
                        }}
                    >
                        <img src={iconPlus} alt="Increment" />
                    </button>
                </div>
            </div>
        );
    };

    const renderMessage = (error): React.Node => {
        return (
            <div className="lightbox is-active" aria-modal="true">
                <div className="lightbox__contents">
                    <div className="lightbox__container">
                        <div className="container-fluid container-fluid--small center-xs">
                            <div className="heading-2">
                                Thank you for your order!
                            </div>
                            <p>{props.message}</p>
                            <button
                                type="button"
                                className="button"
                                onClick={e => {
                                    setFormLightbox(false);
                                    window.location.href = props.redirect_url;
                                }}
                            >
                                <span className="button__text">Done</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <ReactForm
            validators={[]}
            pageSubmit
            method="POST"
            action={props.endpoint_url || ""}
            onSuccess={handleFormSuccess}
            ref={formRef}
        >
            {formLightbox && renderMessage()}
            <div className="resource-field__outer">
                <h2 className="heading-3">Resource materials</h2>
                {props.resource_data.map((data, key): React.Node => {
                    return renderResourceField(data, key);
                })}
            </div>
            <Row>
                <Col xs={12} lg={8}>
                    <h2 className="heading-3 toolkit-content__form-title">
                        Your details
                    </h2>
                    {formError && (
                        <p className="toolkit-content__form-error">
                            {formError}
                        </p>
                    )}
                    <Row>
                        <Col xs={12} sm={6}>
                            <ReactFormField
                                type="text"
                                name="first_name"
                                required={true}
                                errorMessages={{
                                    required: "Please enter a first name.",
                                }}
                                label="First name"
                                placeholder="First name"
                                maxLength="255"
                                defaultValue={props.user_data.first_name}
                            />
                        </Col>

                        <Col xs={12} sm={6}>
                            <ReactFormField
                                type="text"
                                name="last_name"
                                required={true}
                                errorMessages={{
                                    required: "Please enter a last name.",
                                }}
                                label="Last name"
                                placeholder="Last name"
                                maxLength="255"
                                defaultValue={props.user_data.last_name}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <ReactFormField
                                type="number"
                                name="contact_number"
                                required={true}
                                errorMessages={{
                                    required: "Please enter a contact number.",
                                }}
                                label="Contact Number"
                                placeholder="Contact Number"
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <ReactFormField
                                type="email"
                                name="email_address"
                                required={true}
                                validators={["email"]}
                                errorMessages={{
                                    required: "Please enter an email.",
                                }}
                                label="Email Address"
                                placeholder="Email Address"
                                defaultValue={props.user_data.email}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <ReactFormField
                                type="text"
                                name="hospital_facility_name"
                                required={true}
                                errorMessages={{
                                    required: "Hospital/Facility Name",
                                }}
                                label="Hospital/Facility Name"
                                placeholder="Hospital/Facility Name"
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <ReactFormField
                                type="text"
                                name="department_ward"
                                required={true}
                                errorMessages={{
                                    required: "Department/Ward",
                                }}
                                label="Department/Ward"
                                placeholder="Department/Ward"
                            />
                        </Col>
                    </Row>

                    <div className="form-field-wrapper address">
                        <ReactFormField
                            fieldComponent={GoogleAddressInput}
                            type="text"
                            name="delivery_address"
                            required={!fullAddressEditing}
                            errorMessages={{
                                required: "Please enter and select an address.",
                            }}
                            label="Delivery Address"
                            placeholder={`${
                                !fullAddressEditing
                                    ? "Sydney CBD, NSW 2000, Australia"
                                    : ""
                            }`}
                            onChange={handleGoogleAddressInputChange}
                            autoComplete="off"
                            disabled={fullAddressEditing}
                        />
                        <button
                            onClick={handleCantFindAddressClick}
                            type="button"
                            className="text-button text-button__address toolkit-content__form-address-button"
                        >
                            Can't find your address?
                        </button>
                    </div>

                    <div
                        className={`hidden ${
                            fullAddressEditing ? "is-active" : ""
                        }`}
                    >
                        <Row>
                            <Col xs={12} sm={6}>
                                <ReactFormField
                                    ref={deliveryAddressRef}
                                    type="text"
                                    name="street"
                                    label="Street"
                                    placeholder="street"
                                />
                            </Col>

                            <Col xs={12} sm={6}>
                                <ReactFormField
                                    ref={cityRef}
                                    type="text"
                                    name="city_suburb"
                                    label="City / Suburb"
                                    placeholder="City / Suburb"
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} sm={6}>
                                <ReactFormField
                                    ref={stateRef}
                                    type="text"
                                    name="state"
                                    label="State"
                                    placeholder="State"
                                />
                            </Col>

                            <Col xs={12} sm={6}>
                                <ReactFormField
                                    ref={postcodeRef}
                                    type="text"
                                    name="postcode"
                                    label="Post Code"
                                    placeholder="Post Code"
                                />
                            </Col>
                        </Row>
                    </div>

                    <ReactFormField
                        type="textarea"
                        name="specific_delivery_instructions"
                        label="Specific Delivery Instructions"
                        placeholder="Specific Delivery Instructions"
                    />
                    <p>
                        Please allow 7 - 10 working days for delivery of
                        resources. Quantities on some items are limited and may
                        be revised.
                    </p>
                    <div>
                        <button
                            type="submit"
                            className="button toolkit-content__form-submit"
                        >
                            Submit
                        </button>
                    </div>
                    {props.hidden.map((input, index): any => {
                        return (
                            <div
                                key={index}
                                dangerouslySetInnerHTML={{ __html: input }}
                            ></div>
                        );
                    })}
                    <input
                        type="hidden"
                        name="_triggering_element_name"
                        value="op"
                    />
                    <input
                        type="hidden"
                        name="_triggering_element_value"
                        value="Submit"
                    />
                </Col>
            </Row>
        </ReactForm>
    );
};

export default ToolkitOrderForm;
